<template>
  <el-popover
    v-model:visible="visible"
    :disabled="disabled"
    :width="popoverWidth"
    trigger="click"
    placement="bottom-start"
    :show-arrow="false"
    :teleported="true"
    :popper-class="`step-time-picker-popper ${popperClass}`"
    @after-enter="scrollToSelected"
  >
    <template #reference>
      <div
        :class="['step-time-picker', { 'is-disabled': disabled, 'is-active': visible, 'is-empty': !inputText }]"
        @mouseenter="boxHover = true"
        @mouseleave="boxHover = false"
      >
        <input
          ref="inputRef"
          class="stp-input"
          :value="inputText"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="!editable"
          inputmode="numeric"
          maxlength="5"
          @input="handleInput"
          @keydown.enter.prevent="handleInputEnter"
        />
        <el-icon
          v-if="showIcon"
          class="stp-icon"
          :class="{ 'is-clear': showClear }"
          @click="onIconClick"
        >
          <CircleClose v-if="showClear" />
          <Clock v-else />
        </el-icon>
      </div>
    </template>

    <div class="stp-panel">
      <div class="stp-cols">
        <div ref="hourColRef" class="stp-col">
          <div
            v-for="h in hourList"
            :key="'h' + h"
            :class="['stp-cell', { 'is-selected': h === draftHour }]"
            @click="selectHour(h)"
          >
            {{ pad(h) }}
          </div>
        </div>
        <div ref="minuteColRef" class="stp-col">
          <div
            v-for="m in minuteList"
            :key="'m' + m"
            :class="['stp-cell', { 'is-selected': m === draftMinute }]"
            @click="selectMinute(m)"
          >
            {{ pad(m) }}
          </div>
        </div>
      </div>
      <!-- autoCommit 模式下时/分选完即自动提交关闭，无需确认按钮 -->
      <div v-if="!autoCommit" class="stp-footer">
        <el-button class="stp-confirm-button" type="primary" size="small" :disabled="!canConfirm" @click="confirm">
          {{ confirmText || t('common.confirm1') }}
        </el-button>
      </div>
    </div>
  </el-popover>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import { Clock, CircleClose } from '@element-plus/icons-vue'
import { useI18n } from 'vue-i18n'

interface Props {
  modelValue?: string
  // 允许选择的小时列表
  hourOptions?: number[]
  // 允许选择的分钟：数组或依赖当前小时的函数（用于结束时间等联动约束）
  minuteOptions?: number[] | ((hour: number) => number[])
  // 未提供 minuteOptions 时，用步长生成分钟列表
  minuteStep?: number
  placeholder?: string
  disabled?: boolean
  showIcon?: boolean
  popoverWidth?: number
  popperClass?: string
  confirmText?: string
  // 是否允许手动输入（默认允许）
  editable?: boolean
  // 无选中值时，打开弹窗默认定位到的起始时间（该时间置于第一行，便于选择）
  defaultScrollTime?: string
  /**
   * 免确认模式：时、分都选中后自动提交并关闭菜单；点击空白处关闭时也提交，
   * 未选中的部分用 defaultScrollTime 的小时（默认 09）与 00 分补全
   */
  autoCommit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  hourOptions: () => Array.from({ length: 24 }, (_, i) => i),
  minuteOptions: undefined,
  minuteStep: 30,
  placeholder: 'HH:MM',
  disabled: false,
  showIcon: true,
  popoverWidth: 120,
  popperClass: '',
  confirmText: '',
  editable: false,
  defaultScrollTime: '09:00',
  autoCommit: false
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'change', value: string): void
}>()

const { t } = useI18n()

const ITEM_HEIGHT = 32

const visible = ref(false)
const hourColRef = ref<HTMLElement>()
const minuteColRef = ref<HTMLElement>()
const inputRef = ref<HTMLInputElement>()

// 鼠标悬停到整个输入框：有值时展示清除图标
const boxHover = ref(false)

// 草稿态：菜单打开期间的临时选择，仅点击“确认”/回车后才提交到 modelValue
const draftHour = ref<number | null>(null)
const draftMinute = ref<number | null>(null)

// 输入框文本：菜单打开时反映草稿（选择或键入）；关闭时回落到已提交值
const inputText = ref(props.modelValue || '')

const pad = (n: number) => String(n).padStart(2, '0')

const parseValue = (val?: string) => {
  const match = /^(\d{1,2}):(\d{1,2})$/.exec(val || '')
  if (!match) return { hour: null as number | null, minute: null as number | null }
  return { hour: Number(match[1]), minute: Number(match[2]) }
}

const hourList = computed(() => props.hourOptions ?? [])

const resolveMinutes = (hour: number | null): number[] => {
  const mo = props.minuteOptions
  if (typeof mo === 'function') {
    return hour == null ? [] : mo(hour)
  }
  if (Array.isArray(mo)) return mo
  const step = props.minuteStep > 0 ? props.minuteStep : 30
  const list: number[] = []
  for (let i = 0; i < 60; i += step) list.push(i)
  return list
}

// 分钟列以当前草稿小时为准；未选小时时用首个可选小时兜底展示
const activeHour = computed(() => draftHour.value ?? hourList.value[0] ?? 0)
const minuteList = computed(() => resolveMinutes(activeHour.value))

// 将文本解析为 { hour, minute }，支持 "9:30" / "0930" / "930" / "9" 等写法
const parseInput = (raw: string): { hour: number; minute: number } | null => {
  const text = (raw || '').trim().replace('：', ':')
  if (!text) return null
  if (text.includes(':')) {
    const [h, m] = text.split(':')
    if (h === '') return null
    const hour = Number(h)
    const minute = m === '' ? 0 : Number(m)
    if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null
    return { hour, minute }
  }
  if (!/^\d+$/.test(text)) return null
  if (text.length <= 2) return { hour: Number(text), minute: 0 }
  if (text.length === 3) return { hour: Number(text.slice(0, 1)), minute: Number(text.slice(1)) }
  if (text.length === 4) return { hour: Number(text.slice(0, 2)), minute: Number(text.slice(2)) }
  return null
}

// 校验并对齐到可选项：小时必须在允许列表内，分钟就近对齐到允许的分钟
const clampToOptions = (hour: number, minute: number): { hour: number; minute: number } | null => {
  if (hour > 23 || minute > 59 || hour < 0 || minute < 0) return null
  if (!hourList.value.includes(hour)) return null
  const minutes = resolveMinutes(hour)
  if (minutes.length === 0) return null
  if (!minutes.includes(minute)) {
    minute = minutes.reduce(
      (best, m) => (Math.abs(m - minute) < Math.abs(best - minute) ? m : best),
      minutes[0]
    )
  }
  return { hour, minute }
}

// 从输入文本解析出合法可提交的时间（对齐可选项），否则返回 null
const resolveInput = (raw: string) => {
  const p = parseInput(raw)
  if (!p) return null
  return clampToOptions(p.hour, p.minute)
}

// 计算可提交的最终值：空串表示清空（合法）；非法输入返回 null（不可提交）
const getCommitValue = (raw: string): string | null => {
  if (!(raw || '').trim()) return ''
  const res = resolveInput(raw)
  if (!res) return null
  return `${pad(res.hour)}:${pad(res.minute)}`
}

// 可确认：当前输入可解析为合法时间，或为空（清空）
const canConfirm = computed(() => getCommitValue(inputText.value) != null)

// 悬停输入框且有值（非禁用）时展示清除图标
const showClear = computed(() => props.showIcon && !props.disabled && !!inputText.value && boxHover.value)

// 已提交值变化时同步输入框（confirm 内已即时更新，此处兜底外部修改）
watch(
  () => props.modelValue,
  (v) => {
    inputText.value = v || ''
  }
)

// 本次打开期间用户是否做过选择/输入：未操作过就关闭菜单时不做任何补全提交
const touched = ref(false)

// autoCommit 补全：未选的小时回落到 defaultScrollTime 的小时（默认 09），未选的分钟回落到 00；
// 两者都需对齐到当前可选项（可选小时受日期/对侧时间约束，未必包含 09）
const resolveFallback = (hour: number | null, minute: number | null) => {
  const hours = hourList.value
  if (hours.length === 0) return null
  let h = hour
  if (h == null || !hours.includes(h)) {
    const { hour: defaultHour } = parseValue(props.defaultScrollTime)
    const i = defaultHour == null ? 0 : Math.max(findScrollIndex(hours, defaultHour), 0)
    h = hours[i]
  }
  const minutes = resolveMinutes(h)
  if (minutes.length === 0) return null
  let m = minute
  if (m == null || !minutes.includes(m)) {
    m = minutes.includes(0) ? 0 : minutes[0]
  }
  return { hour: h, minute: m }
}

// 提交草稿（含补全），用于 autoCommit 模式下的选完即提交与点击空白处关闭
const commitDraft = () => {
  const res = resolveFallback(draftHour.value, draftMinute.value)
  if (!res) return
  const val = `${pad(res.hour)}:${pad(res.minute)}`
  inputText.value = val
  // 提交后清除操作标记，避免随后的关闭动作重复提交同一个值
  touched.value = false
  if (val !== (props.modelValue || '')) {
    emit('update:modelValue', val)
    emit('change', val)
  }
}

// 打开菜单：用已提交值初始化草稿与输入框
// 关闭时：autoCommit 且用户有操作则补全提交，否则丢弃未确认内容回落到已提交值
watch(visible, (open) => {
  if (open) {
    const { hour, minute } = parseValue(props.modelValue)
    draftHour.value = hour
    draftMinute.value = minute
    inputText.value = props.modelValue || ''
    touched.value = false
  } else if (props.autoCommit && touched.value) {
    commitDraft()
  } else {
    inputText.value = props.modelValue || ''
  }
})

// 草稿变化时同步输入框显示
// autoCommit 下只选中一侧也要实时显示，另一侧按补全规则预览（与关闭时实际提交值一致）
const syncInputFromDraft = () => {
  if (props.autoCommit) {
    if (draftHour.value == null && draftMinute.value == null) {
      inputText.value = ''
      return
    }
    const res = resolveFallback(draftHour.value, draftMinute.value)
    if (res) inputText.value = `${pad(res.hour)}:${pad(res.minute)}`
    return
  }
  if (draftHour.value != null && draftMinute.value != null) {
    inputText.value = `${pad(draftHour.value)}:${pad(draftMinute.value)}`
  }
}

const selectHour = (hour: number) => {
  draftHour.value = hour
  touched.value = true
  if (props.autoCommit) {
    syncInputFromDraft()
    // 时、分都已选中：提交并关闭
    if (draftMinute.value != null) {
      commitDraft()
      visible.value = false
    }
    return
  }
  // 空状态先选小时：分钟自动选中 00；已有分钟则保持不变
  if (draftMinute.value == null) draftMinute.value = 0
  syncInputFromDraft()
}

const selectMinute = (minute: number) => {
  draftMinute.value = minute
  touched.value = true
  if (props.autoCommit) {
    syncInputFromDraft()
    // 时、分都已选中：提交并关闭
    if (draftHour.value != null) {
      commitDraft()
      visible.value = false
    }
    return
  }
  // 空状态先选分钟：小时自动选中 00；已有小时则保持不变
  if (draftHour.value == null) draftHour.value = 0
  syncInputFromDraft()
}

// 手动输入：仅保留数字与冒号，并实时联动草稿高亮
const handleInput = (e: Event) => {
  const el = e.target as HTMLInputElement
  const v = el.value.replace(/[^\d:：]/g, '').replace('：', ':')
  inputText.value = v
  el.value = v
  touched.value = true
  if (!v) {
    // 退格清空：立即清除草稿并触发内容变更
    draftHour.value = null
    draftMinute.value = null
    // 主动清空是明确意图，关闭菜单时不应再按默认值补全回来
    touched.value = false
    if (props.modelValue) {
      emit('update:modelValue', '')
      emit('change', '')
    }
    return
  }
  const res = resolveInput(v)
  if (res) {
    draftHour.value = res.hour
    draftMinute.value = res.minute
  }
}

// 点击确认/回车才生效（点击空白处关闭菜单则草稿丢弃，不提交）
const confirm = () => {
  const val = getCommitValue(inputText.value)
  if (val === null) return
  inputText.value = val
  if (val !== (props.modelValue || '')) {
    emit('update:modelValue', val)
    emit('change', val)
  }
  visible.value = false
}

const handleInputEnter = () => {
  confirm()
}

// 清空当前值（清除已提交值与草稿）
const handleClear = () => {
  inputText.value = ''
  draftHour.value = null
  draftMinute.value = null
  // 主动清空是明确意图，关闭菜单时不应再按默认值补全回来
  touched.value = false
  boxHover.value = false
  if (props.modelValue) {
    emit('update:modelValue', '')
    emit('change', '')
  }
}

// 图标点击：展示清除图标时执行清空并阻止冒泡（避免触发 popover 开关）；否则交由 popover 处理
const onIconClick = (e: MouseEvent) => {
  if (showClear.value) {
    e.stopPropagation()
    handleClear()
  }
}

// 在列表中查找目标值的索引：不存在时就近取第一个 >= 目标的项，兜底取最后一项
const findScrollIndex = (list: number[], target: number): number => {
  if (list.length === 0) return -1
  const exact = list.indexOf(target)
  if (exact >= 0) return exact
  const ge = list.findIndex((v) => v >= target)
  return ge >= 0 ? ge : list.length - 1
}

const scrollToSelected = () => {
  nextTick(() => {
    if (hourColRef.value) {
      if (draftHour.value != null) {
        const i = hourList.value.indexOf(draftHour.value)
        if (i >= 0) hourColRef.value.scrollTop = i * ITEM_HEIGHT
      } else {
        // 无选中值：将默认起始时间的小时定位到第一行，便于选择
        const { hour } = parseValue(props.defaultScrollTime)
        const i = hour == null ? -1 : findScrollIndex(hourList.value, hour)
        hourColRef.value.scrollTop = i >= 0 ? i * ITEM_HEIGHT : 0
      }
    }
    if (minuteColRef.value && draftMinute.value != null) {
      const i = minuteList.value.indexOf(draftMinute.value)
      if (i >= 0) minuteColRef.value.scrollTop = i * ITEM_HEIGHT
    }
  })
}
</script>

<style scoped>
.step-time-picker {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #495057;
  user-select: none;
  line-height: 1;
}

.stp-input {
  flex: 1 1 auto;
  width: 48px;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font-size: inherit;
  color: #495057;
  cursor: text;
}

.stp-input::placeholder {
  color: #a8abb2;
}

.step-time-picker.is-disabled {
  cursor: not-allowed;
  color: #c0c4cc;
}

.step-time-picker.is-disabled .stp-input {
  cursor: not-allowed;
  color: #c0c4cc;
}

.stp-icon {
  font-size: 14px;
  color: #86909c;
  flex-shrink: 0;
}

.stp-icon.is-clear {
  cursor: pointer;
  color: #a8abb2;
}

.stp-icon.is-clear:hover {
  color: #86909c;
}

.stp-panel {
  display: flex;
  flex-direction: column;
}

.stp-cols {
  display: flex;
  height: 224px;
}

.stp-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  padding: 4px 0;
  border-right: 1px solid #f0f0f0;
  /* 隐藏滚动条但保留滚动能力 */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.stp-col:last-child {
  border-right: none;
}

.stp-col::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.stp-cell {
  width: 52px;
  height: 32px;
  line-height: 32px;
  text-align: center;
  font-size: 14px;
  color: #495057;
  cursor: pointer;
  border-radius: 4px;
  flex-shrink: 0;
  transition: background-color 0.15s;
}

.stp-cell:hover {
  background-color: #f5f5f5;
}

.stp-cell.is-selected {
  background-color: #f2ecfd;
  color: #8755de;
  font-weight: 600;
}

.stp-footer {
  display: flex;
  justify-content: flex-end;
  padding: 8px 8px;
  border-top: 1px solid #f0f0f0;
  .stp-confirm-button {
    width: 54px;
    height: 24px;
    font-size: 14px;
    font-weight: normal;
    line-height: 22px;
  }
}
</style>

<style>
.step-time-picker-popper.el-popover.el-popper {
  padding: 0;
  min-width: unset;
}
</style>
